import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';

import { FeatureFlag } from 'constants/featureFlags';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { checkIsDirectPendingChange } from 'utils/pendingChanges';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getLoanAppraisalSurchargesAction } from 'store/loanApplications/actions';
import {
  selectCommonInfoLoanApplication,
  selectExtendedBorrowersCommonInfo,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary,
  selectIsLoanTBDBundleIssued,
  selectIsPropertyEnrichmentLoading,
  selectIsPurchaseLoanPurposeFromApplication,
  selectLoanAppraisalSurcharges,
  selectPropertyEnrichmentData,
} from 'store/loanApplications/selectors';
import { getAppraisalProductsAction } from 'store/system/actions';
import { selectAppraisalProductsValues } from 'store/system/selectors';

import { PROPERTY_SECTION_HREF_MAP } from '../../../EditModal.hrefs';
import useMirroredFormValue from '../../../hooks/useMirroredFormValue';
import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';
import EnrichmentFieldIndicators from '../Property/components/EnrichmentFieldIndicators';
import { getAppraisalEnrichmentValuesMap } from '../Property/enrichmentFieldMapping';
import { propertyConfigFields } from '../Property/Property.config';

import { appraisalConfigFields, getAppraisalConfig } from './Appraisal.config';
import {
  buildAppraisalProductOptions,
  buildAppraisalSurchargeRows,
  splitAppraisalConfigByCardMarkers,
} from './Appraisal.utils';
import AppraisalOrderContactsCard from './components/AppraisalOrderContactsCard';
import AppraisalSurchargesCard from './components/AppraisalSurchargesCard';

const Appraisal = ({
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  className,
  applicationData,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  entitySideData,
  setEntitySideData,
  setInitialEntitySideData,
  setIsSavingAllowed,
  setIsNestedEntityEditActive,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const formContainerRef = useRef(null);
  const loanId = useLoanId();
  const { applicationId, tab, subtab } = useParams();
  const isOrderContactsEnabled = useFeatureToggle(FeatureFlag.EnableAppraisalOrderContacts);
  const isOtherOrderContactsEnabled = useFeatureToggle(
    FeatureFlag.EnableAppraisalOrderOtherContacts,
  );
  const { loanType } = useSelector(selectCommonInfoLoanApplication);
  const isLoanTBDBundleIssued = useSelector(selectIsLoanTBDBundleIssued);
  const enrichmentData = useSelector(selectPropertyEnrichmentData);
  const isEnrichmentLoading = useSelector(selectIsPropertyEnrichmentLoading);
  const borrowers = useSelector(selectExtendedBorrowersCommonInfo);
  const isPurchase = useSelector(selectIsPurchaseLoanPurposeFromApplication);
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );

  const {
    isViewAllowed: isAppraisalSelectionViewAllowed,
    isEditAllowed: isAppraisalSelectionEditAllowed,
  } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_APPRAISAL_SELECTION));

  const appraisalProducts = useSelector(selectAppraisalProductsValues);
  const loanAppraisalSurcharges = useSelector(selectLoanAppraisalSurcharges);

  const isAgentOrderContactsVisible = isPurchase || (isAdditionalLoan && isHelocPurposePurchase);
  const isOrderContactsCardVisible = isAppraisalSelectionViewAllowed && isOrderContactsEnabled;

  const appraisalProductOptions = useMemo(
    () => buildAppraisalProductOptions(appraisalProducts),
    [appraisalProducts],
  );

  const appraisalSurchargeRows = useMemo(
    () => buildAppraisalSurchargeRows(loanAppraisalSurcharges),
    [loanAppraisalSurcharges],
  );

  useMirroredFormValue({
    form,
    setInForm,
    path: appraisalConfigFields.locationType.nameInModel,
    mirrorPath: propertyConfigFields.locationType.nameInModel,
  });

  useEffectSafeStrictMode(() => {
    if (!isAppraisalSelectionViewAllowed) {
      return;
    }

    dispatch(getAppraisalProductsAction());
  }, [dispatch, isAppraisalSelectionViewAllowed]);

  useEffectSafeStrictMode(() => {
    if (!isAppraisalSelectionViewAllowed || !loanId) {
      return;
    }

    dispatch(getLoanAppraisalSurchargesAction({ loanId, applicationId }));
  }, [dispatch, isAppraisalSelectionViewAllowed, loanId, applicationId]);

  const enrichmentValuesMap = useMemo(
    () => getAppraisalEnrichmentValuesMap(enrichmentData),
    [enrichmentData],
  );

  const numberOfUnits = Number(
    (form.propertyType !== undefined ? form.numberOfUnits?.value : applicationData.numberOfUnits) ??
      1,
  );

  const config = useMemo(
    () =>
      getAppraisalConfig({
        numberOfUnits,
        loanType,
        isLoanTBDBundleIssued,
        appraisalProductOptions,
        isAppraisalSelectionViewAllowed,
        isAppraisalSelectionEditAllowed,
      }),
    [
      numberOfUnits,
      loanType,
      isLoanTBDBundleIssued,
      appraisalProductOptions,
      isAppraisalSelectionViewAllowed,
      isAppraisalSelectionEditAllowed,
    ],
  );

  const { firstRenderConfig, secondRenderConfig } = useMemo(
    () => splitAppraisalConfigByCardMarkers(config.groups),
    [config],
  );

  const isSurchargesFieldChanged =
    !disabledPendingChangesHighlight &&
    checkIsDirectPendingChange(pendingChanges?.appraisalSurcharges);

  const onOpenAgentAndSeller = useCallback(
    () =>
      history.push(
        LoansPaths.applicationManagement.generatePath({
          applicationId,
          tab,
          subtab,
          entityId: PROPERTY_SECTION_HREF_MAP.AGENT_AND_SELLER,
        }),
      ),
    [history, applicationId, tab, subtab],
  );

  useEffect(() => {
    setEntityConfig(config);
  }, [config, setEntityConfig]);

  return (
    <Box className={className}>
      <Box ref={formContainerRef} sx={boxStyle}>
        {isAppraisalSelectionEditAllowed && (
          <EnrichmentFieldIndicators
            containerRef={formContainerRef}
            enrichmentValuesMap={enrichmentValuesMap}
            form={form}
            isLoading={isEnrichmentLoading}
            setInForm={setInForm}
            validateField={validateField}
          />
        )}
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={firstRenderConfig}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.APPRAISAL}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
        {isOrderContactsCardVisible && (
          <AppraisalOrderContactsCard
            agentsAndSellersPendingChanges={pendingChanges?.agentsAndSellersPendingChanges}
            applicationData={applicationData}
            borrowers={borrowers}
            entitySideData={entitySideData}
            isAgentSlotsVisible={isAgentOrderContactsVisible}
            isEditAllowed={isAppraisalSelectionEditAllowed}
            isHighlightDisabled={disabledPendingChangesHighlight}
            isLoading={isSaving || isLoading}
            isOtherContactsEnabled={isOtherOrderContactsEnabled}
            pendingChanges={pendingChanges?.appraisalOrderContacts}
            setEntitySideData={setEntitySideData}
            setInitialEntitySideData={setInitialEntitySideData}
            setIsNestedEntityEditActive={setIsNestedEntityEditActive}
            setIsSavingAllowed={setIsSavingAllowed}
            onOpenAgentAndSeller={onOpenAgentAndSeller}
          />
        )}
        {isAppraisalSelectionViewAllowed && (
          <AppraisalSurchargesCard
            isFieldChanged={isSurchargesFieldChanged}
            surcharges={appraisalSurchargeRows}
          />
        )}
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={secondRenderConfig}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.APPRAISAL}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>
    </Box>
  );
};

Appraisal.propTypes = pagesPropTypes;

export default memo(Appraisal);
